import {
  ActionsPopup,
  IStyles,
  QuestionContainerWithSolution,
  QuestionSourceTags,
  deserify,
  getDeviceType,
  getLocalStorage,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import {
  DifficultyLevelEnum,
  Question,
} from '@protos/content_management/content.db_pb';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../../../Home/reducer/homeDashboard.slice';
import {
  addModuleFilteredQuestion,
  deleteModuleFilteredQuestionsById,
  setFAQuestionModuleMap,
  setModuleFilteredQuestions,
  setQuestionsCount,
  setSelectedTasksInfo,
} from '../../../reducer/assessmentFlow.slice';
import {
  canEdit,
  createQuestionCountForDifficultyLevel,
  deduceMaxQuestionsCount,
  getDifficultyLevelFromString,
  moveQuestionId,
  rearrangeTaskInfoArrayAfterDeletion,
  rearrangeTaskInfoArrayAfterMove,
} from '../functions';
import { getMiddleApproximateTime } from '../../../utils/assessment.utils';
import InterractiveButtonGroup from './InterractiveButtonGroup';
import {
  AssessmentCreationEnum,
  TaskInfoModel,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import {
  AssessmentEnum,
  MarkingSchemeEnum,
} from '@protos/learning_management/lms.db_pb';

const styles: IStyles = {
  root: {
    bgcolor: 'common.white',
    padding: { xs: pxToRem(20), md: pxTovW(17) },
  },
  heading: {
    padding: { md: pxTovW(17) },
    borderBottom: '1px solid lightgrey',
  },
  questionsContainer: {
    width: '100%',
    padding: pxTovW(20),
    display: 'flex',
    flexDirection: 'column',
    gap: pxTovW(10),
    boxSizing: 'border-box',
  },
  questionWrapper: {
    // margin: pxTovW(20),
    // height: {xs: pxToRem(250), md: pxTovW(470)},
    // width: { md: pxTovW(944) },
    width: '100%',
    // bgcolor: 'neutral.lightBlue',
    position: 'relative',
    border: '1px solid #CED2FC',
    marginBottom: { xs: pxToRem(20), md: pxTovW(30) },
    borderRadius: {
      xs: pxToRem(15),
      md: pxTovW(15),
    },
    overflow: 'hidden',
  },
  questionDetailsWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: {
      xs: pxToRem(5),
      md: pxTovW(5),
    },
  },
  marksWrapper: {
    padding: {
      xs: `${pxToRem(5)} ${pxToRem(13)}`,
      md: `${pxTovW(7)} ${pxTovW(11)}`,
    },
    borderRadius: {
      xs: pxToRem(16),
      md: pxTovW(35),
    },
    marginLeft: {
      xs: pxToRem(10),
      md: pxTovW(14),
    },
    border: '1px solid #CEEAFF',
  },
  marks: {
    fontWeight: 700,
  },
  questionPart: {
    padding: {
      xs: `${pxToRem(24)} ${pxToRem(11)}`,
      md: `${pxTovW(24)} ${pxTovW(50)}`,
    },
  },
  correctAnswerWrapper: {
    borderTop: '1px solid #CED2FC',
    padding: {
      xs: `${pxToRem(15)} ${pxToRem(13)}`,
      md: `${pxTovW(24)} ${pxTovW(30)}`,
    },
    backgroundColor: '#F3F9FE',
  },
};

interface IProps {
  title: string | React.ReactNode;
  subtitle?: string;
  isDiagnostic?: boolean;
  isFormative?: boolean;
}
export default function QuestionsList(props: IProps) {
  const { title, subtitle, isDiagnostic, isFormative } = props;
  const dispatch = useAppDispatch();
  const {
    questions_stats,
    module_filtered_questions,
    selected_tasks_info,
    fetched_assessment_details,
    fa_selected_chapter_data,
    fa_assessment_config,
    assesment_creation_type,
    fa_question_module_map,
    fa_format_details,
  } = deserify(useAppSelector((state) => state.assessment));
  const questionModuleMap = fa_question_module_map || [];
  const { class_subject_info } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );
  const { manage_mode_of_conduction } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const android = window.Android;
  const deviceType = getDeviceType();
  const teacher_id = getLocalStorage('userId');
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { module_id } = useParams();
  const [quesLoading, setQuesLoading] = useState(false);
  const [quesError, setQuesError] = useState(false);
  const [interactiveButtonLoading, setInteractiveButtonLoading] =
    useState(false);
  const [idTobeDeleted, setIdtobeDeleted] = useState('');
  const [actionPopupState, setActionPopupState] = useState(false);
  const [replacedQuestionIds, setReplacedQuestionIds] = useState<string[]>([]);
  const { LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler } =
    useCommonServiceClientContext();

  // --------------------------------------------------------------

  useEffect(() => {
    if (module_filtered_questions?.questions) {
      // setQuestionsList(module_filtered_questions.questions);
      dispatch(setQuestionsCount());
    }
  }, [
    module_filtered_questions?.questions
      ?.map((question) => question.questionId)
      .sort()
      .join(','),
  ]);

  useEffect(() => {
    try {
      if (deviceType === 'android' && android?.disableAppRefresh) {
        android.disableAppRefresh();
        console.log('App refresh enabled on Android for home page');
      }
    } catch (error) {
      console.log(
        'An error occurred while attempting to enable app refresh on Android:'
      );
    }
  }, []);

  // --------------------------------------------------------------

  const deletePopupOpen = (questionIdToDelete?: string) => {
    if (!questionIdToDelete) {
      return;
    }
    setIdtobeDeleted(questionIdToDelete);
    setActionPopupState(true);
  };

  // --------------------------------------------------------------

  const handleDeleteQuestion = (questionIdToDelete?: string) => {
    if (!questionIdToDelete) {
      return;
    }
    dispatch(deleteModuleFilteredQuestionsById(questionIdToDelete));
    if (selected_tasks_info) {
      const res = rearrangeTaskInfoArrayAfterDeletion(
        questionIdToDelete,
        selected_tasks_info
      );
      dispatch(setSelectedTasksInfo(res));
    }
    dispatch(setQuestionsCount());
    setActionPopupState(false);
    const newQuestionsModuleMap = questionModuleMap.filter(
      (q) => q.questionId !== questionIdToDelete
    );
    dispatch(setFAQuestionModuleMap(newQuestionsModuleMap));
    dispatch(
      setToastInfo({
        label: 'Question deleted successfully',
        variant: 'info',
        open: true,
      })
    );
  };

  // --------------------------------------------------------------

  function mapQuestionsBySequence(
    questions: Question[],
    task_info_array?: TaskInfoModel[]
  ): Question[] {
    if (!task_info_array) {
      return questions;
    }
    console.log('task_info_array', task_info_array);
    const newTaskArray = task_info_array.slice();
    newTaskArray.sort((a, b) => a.sequence - b.sequence);
    const sortedQuestionIds = newTaskArray.map((task) => [
      task.questionId,
      task.maxMarks,
    ]);
    const mappedQuestions = sortedQuestionIds.map((ques) => {
      const question = questions.find(
        (question) => question.questionId === ques[0]
      );
      if (question?.question?.model?.value?.commonQuestionContent) {
        question.question.model.value.commonQuestionContent.marks = [
          Number(ques[1] ?? 1),
        ];
      }
      return new Question({ ...question });
    });
    return mappedQuestions;
  }

  // --------------------------------------------------------------

  const replaceQuestion = async (ques: Question) => {
    try {
      setInteractiveButtonLoading(true);
      if (
        !ques.questionMeta ||
        ques.questionMeta.difficultyLevel ===
          DifficultyLevelEnum.DIFFICULTY_LEVEL_UNDEFINED
      ) {
        setInteractiveButtonLoading(false);
        return;
      }
      //Handling the case of already exhausting the max number of questions in selected difficluty type
      const totalQuestionsOfCategory =
        module_filtered_questions?.questions.filter(
          (q) =>
            q.questionMeta?.difficultyLevel ===
            ques.questionMeta?.difficultyLevel
        );
      const maxQuestionsOfCategory = deduceMaxQuestionsCount(
        ques?.questionMeta.difficultyLevel,
        questions_stats?.questionDifficultyLevel
      );
      if (totalQuestionsOfCategory?.length === maxQuestionsOfCategory) {
        const level =
          ques.questionMeta.difficultyLevel ===
          DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH
            ? 'Hard'
            : ques.questionMeta.difficultyLevel ===
              DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM
            ? 'Medium'
            : 'Easy';
        dispatch(
          setToastInfo({
            variant: 'info',
            label: `There are no ${level}-level questions available`,
            open: true,
          })
        );

        setInteractiveButtonLoading(false);
        return;
      }
      //For the case of more questions available
      const newQuestionCount = createQuestionCountForDifficultyLevel(
        ques?.questionMeta.difficultyLevel,
        questions_stats?.questionDifficultyLevel
      );

      // Get the bloom level from the question being replaced
      const questionBloomLevels = ques.questionMeta?.bloomLevel
        ? [ques.questionMeta?.bloomLevel]
        : undefined;

      // First, try to get the moduleId from questionModuleMap
      const questionMapEntry = questionModuleMap.find(
        (q) => q.questionId === ques.questionId
      );

      let response;
      let useFallback1 = false;
      let useFallback2 = false;

      // Attempt 1: specific moduleId + bloom level of the question
      if (
        questionMapEntry &&
        (questionMapEntry.topicId || questionMapEntry.chapterId)
      ) {
        const specificModuleId =
          questionMapEntry.topicId ?? questionMapEntry.chapterId;
        if (specificModuleId) {
          response =
            await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
              {
                modeOfConduction:
                  assesment_creation_type?.creationType === 'Readymade'
                    ? manage_mode_of_conduction
                    : assesment_creation_type.modeOfConduction,
                teacherId: teacher_id,
                subjectId: class_subject_info?.subjectId,
                sectionId: isFormative
                  ? fa_format_details?.selectedSection?.id
                  : fetched_assessment_details?.sectionId ||
                    class_subject_info?.sectionId,
                assessmentCreationType: isFormative
                  ? AssessmentCreationEnum.ASSESSMENT_CREATION_FORMATIVE
                  : AssessmentCreationEnum.ASSESSMENT_CREATION_HOMEWORK,
                noOfQuestions: fa_assessment_config?.noOfQuestions,
                approximateTime: getMiddleApproximateTime({
                  difficulty: fa_assessment_config?.overallDifficultyLevel,
                  questionCount: fa_assessment_config?.noOfQuestions,
                }),
                avgDifficultyLevel: getDifficultyLevelFromString(
                  fa_assessment_config?.overallDifficultyLevel || 'Medium'
                ),
                moduleIds: [specificModuleId],
                requiredDifficultyLevelsCountInfo: newQuestionCount,
                qIdsToBeIgnored: module_filtered_questions
                  ? [
                      ...module_filtered_questions.questions.map(
                        (e) => e.questionId || ''
                      ),
                      ...replacedQuestionIds,
                    ]
                  : undefined,
                bloomLevels: questionBloomLevels,
                requiredQuestionTypes: fa_assessment_config?.QuestionTypes,
              }
            );

          // If no questions found, use fallback 1
          if (
            !response.data?.questions ||
            response.data.questions.length === 0
          ) {
            useFallback1 = true;
          }
        } else {
          useFallback1 = true;
        }
      } else {
        useFallback1 = true;
      }

      // Fallback 1: bloom level of the question + all module ids
      if (useFallback1) {
        let moduleIds = fa_selected_chapter_data // to make difference between formative and homework
          ? fa_selected_chapter_data?.chapters.flatMap((chapter) => [
              chapter.chapterId,
              ...chapter.topics.map((topic) => topic.topicId),
            ])
          : fetched_assessment_details // to make difference between creation and readymade FA
          ? fetched_assessment_details?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo.flatMap(
              (chapter) => [
                chapter.chapterId,
                ...chapter.topicInfo.map((topic) => topic.topicId),
              ]
            )
          : [Number(module_id)];
        // Fallback if still empty
        if (!moduleIds?.length) {
          moduleIds = [Number(module_id)];
        }
        response =
          await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
            {
              modeOfConduction:
                assesment_creation_type?.creationType === 'Readymade'
                  ? manage_mode_of_conduction
                  : assesment_creation_type.modeOfConduction,
              teacherId: teacher_id,
              subjectId: class_subject_info?.subjectId,
              sectionId: isFormative
                ? fa_format_details?.selectedSection?.id
                : fetched_assessment_details?.sectionId ||
                  class_subject_info?.sectionId,
              assessmentCreationType: isFormative
                ? AssessmentCreationEnum.ASSESSMENT_CREATION_FORMATIVE
                : AssessmentCreationEnum.ASSESSMENT_CREATION_HOMEWORK,
              noOfQuestions: fa_assessment_config?.noOfQuestions,
              approximateTime: getMiddleApproximateTime({
                difficulty: fa_assessment_config?.overallDifficultyLevel,
                questionCount: fa_assessment_config?.noOfQuestions,
              }),
              avgDifficultyLevel: getDifficultyLevelFromString(
                fa_assessment_config?.overallDifficultyLevel || 'Medium'
              ),
              moduleIds: moduleIds,
              requiredDifficultyLevelsCountInfo: newQuestionCount,
              qIdsToBeIgnored: module_filtered_questions
                ? [
                    ...module_filtered_questions.questions.map(
                      (e) => e.questionId || ''
                    ),
                    ...replacedQuestionIds,
                  ]
                : undefined,
              bloomLevels: questionBloomLevels,
              requiredQuestionTypes: fa_assessment_config?.QuestionTypes,
            }
          );

        // If no questions found, use fallback 2
        if (!response.data?.questions || response.data.questions.length === 0) {
          useFallback2 = true;
        }
      }

      // Fallback 2: no bloom level + all module ids
      if (useFallback2) {
        let moduleIds = fa_selected_chapter_data // to make difference between formative and homework
          ? fa_selected_chapter_data?.chapters.flatMap((chapter) => [
              chapter.chapterId,
              ...chapter.topics.map((topic) => topic.topicId),
            ])
          : fetched_assessment_details // to make difference between creation and readymade FA
          ? fetched_assessment_details?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo.flatMap(
              (chapter) => [
                chapter.chapterId,
                ...chapter.topicInfo.map((topic) => topic.topicId),
              ]
            )
          : [Number(module_id)];
        // Fallback if still empty
        if (!moduleIds?.length) {
          moduleIds = [Number(module_id)];
        }
        response =
          await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
            {
              modeOfConduction:
                assesment_creation_type?.creationType === 'Readymade'
                  ? manage_mode_of_conduction
                  : assesment_creation_type.modeOfConduction,
              teacherId: teacher_id,
              subjectId: class_subject_info?.subjectId,
              sectionId: isFormative
                ? fa_format_details?.selectedSection?.id
                : fetched_assessment_details?.sectionId ||
                  class_subject_info?.sectionId,
              assessmentCreationType: isFormative
                ? AssessmentCreationEnum.ASSESSMENT_CREATION_FORMATIVE
                : AssessmentCreationEnum.ASSESSMENT_CREATION_HOMEWORK,
              noOfQuestions: fa_assessment_config?.noOfQuestions,
              approximateTime: getMiddleApproximateTime({
                difficulty: fa_assessment_config?.overallDifficultyLevel,
                questionCount: fa_assessment_config?.noOfQuestions,
              }),
              avgDifficultyLevel: getDifficultyLevelFromString(
                fa_assessment_config?.overallDifficultyLevel || 'Medium'
              ),
              moduleIds: moduleIds,
              requiredDifficultyLevelsCountInfo: newQuestionCount,
              qIdsToBeIgnored: module_filtered_questions
                ? [
                    ...module_filtered_questions.questions.map(
                      (e) => e.questionId || ''
                    ),
                    ...replacedQuestionIds,
                  ]
                : undefined,
              bloomLevels: undefined,
              requiredQuestionTypes: fa_assessment_config?.QuestionTypes,
            }
          );
      }

      if (response && response.data?.questions && ques.questionId) {
        if (ques.questionId && !replacedQuestionIds.includes(ques.questionId)) {
          setReplacedQuestionIds([...replacedQuestionIds, ques.questionId]);
        }

        const newQuesList = response.data.questions.filter(
          (que) => que.questionId !== ques.questionId
        );
        // const newQues2 = response.data.questions[0];
        const len = newQuesList.length;
        const newQues2 =
          len > 0 ? newQuesList[Math.floor(Math.random() * len)] : undefined;
        const quesAlreadyExist = module_filtered_questions?.questions.find(
          (ques) => ques.questionId === newQues2?.questionId
        );

        if (response.data.questions.length === 0) {
          dispatch(
            setToastInfo({
              label: 'No questions found to replace',
              variant: 'error',
              open: true,
            })
          );

          setInteractiveButtonLoading(false);
          return;
        } else if (
          !newQues2 ||
          !newQues2.questionId ||
          !selected_tasks_info ||
          quesAlreadyExist
        ) {
          dispatch(
            setToastInfo({
              variant: 'info',
              label: 'Not able to replace the question',
              open: true,
            })
          );

          setInteractiveButtonLoading(false);
          return;
        }

        const newSelectedTasksInfo = moveQuestionId({
          selectedTasksInfoInput: selected_tasks_info,
          oldQuestionId: ques.questionId,
          newQuestionId: newQues2.questionId,
        });
        const resModuleMap = response.data.questionModuleMap || [];
        const newQuestionsModuleMap = questionModuleMap.filter(
          (q) => q.questionId !== ques.questionId
        );
        const newQuestionMap = resModuleMap.find(
          (q) => q.questionId === newQues2.questionId
        );
        if (newQuestionMap) {
          newQuestionsModuleMap.push(newQuestionMap);
        }
        dispatch(setFAQuestionModuleMap(newQuestionsModuleMap));
        dispatch(deleteModuleFilteredQuestionsById(ques.questionId));
        dispatch(addModuleFilteredQuestion(newQues2));
        dispatch(setSelectedTasksInfo(newSelectedTasksInfo));
        dispatch(setQuestionsCount());
      }
      dispatch(
        setToastInfo({
          variant: 'info',
          label: 'Question replaced successfully',
          open: true,
        })
      );
    } catch (err) {
      console.log(err);
      dispatch(
        setToastInfo({
          variant: 'error',
          label: 'Question not replaced !',
          open: true,
        })
      );
    }
    setInteractiveButtonLoading(false);
  };

  // --------------------------------------------------------------

  const onDragEnd = (result: any) => {
    // console.log(result);
    if (!result.destination) return; // Dropped outside the list

    const questionsList = module_filtered_questions?.questions;
    if (!questionsList) {
      return;
    }

    const oldIndex = result.source.index;
    const newIndex = result.destination.index;
    const draggedQues = questionsList[oldIndex];
    const newQuestions = [...questionsList];
    // Remove the dragged ques from its old position
    newQuestions.splice(oldIndex, 1);
    // Insert the dragged ques at the new position
    newQuestions.splice(newIndex, 0, draggedQues);
    // console.log('questionsList2', newQuestions);
    // setQuestionsList(newQuestions);
    dispatch(
      setModuleFilteredQuestions({
        ...module_filtered_questions,
        questions: newQuestions,
      })
    );
    const newTaskSequence = rearrangeTaskInfoArrayAfterMove(
      newQuestions,
      selected_tasks_info
    );
    dispatch(setSelectedTasksInfo(newTaskSequence));
  };

  const updateTaskMarks = (questionId: string, newMarks: number) => {
    if (!selected_tasks_info) return;

    const updated = selected_tasks_info.map((task) =>
      task.questionId === questionId ? { ...task, maxMarks: newMarks } : task
    );

    dispatch(setSelectedTasksInfo(updated));
    // if (module_filtered_questions) {
    //   const updatedQuestions = module_filtered_questions.questions.map((q) => {
    //     if (q.questionId === questionId) {
    //       if (q.question?.model?.value?.commonQuestionContent) {
    //         q.question.model.value.commonQuestionContent.marks = [newMarks];
    //       }
    //     }
    //     return q;
    //   });

    //   dispatch(
    //     setModuleFilteredQuestions({
    //       ...module_filtered_questions,
    //       questions: updatedQuestions,
    //     })
    //   );
    // }
  };
  // --------------------------------------------------------------

  return (
    <Box sx={styles.root}>
      <Box sx={styles.heading}>
        <Box>{title}</Box>
        {subtitle && (
          <Typography
            sx={{ fontSize: { xs: pxToRem(14), md: pxTovW(24) } }}
            color="text.disabled"
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={styles.questionsContainer}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {mapQuestionsBySequence(
                  module_filtered_questions?.questions || [],
                  selected_tasks_info
                ).map((ques, index: number) => {
                  const questionContent = ques.question;
                  const questionCase = questionContent?.model.case;
                  if (!questionCase) {
                    return null;
                  }
                  return (
                    <Draggable
                      // key={'ques_' + index}
                      // draggableId={'ques_' + index}
                      key={ques.questionId}
                      draggableId={ques.questionId || 'ques_' + index}
                      index={index}
                      isDragDisabled={interactiveButtonLoading}
                    >
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            marginTop: { xs: pxToRem(20), md: pxTovW(30) },
                            marginBottom: { xs: pxToRem(20), md: pxTovW(30) },
                            position: 'relative',
                          }}
                        >
                          <QuestionContainerWithSolution
                            questionNumber={index + 1}
                            headerLeftElement={
                              <QuestionSourceTags
                                question={ques}
                                teacherProfileId={teacher_id}
                                teacherName={user_info?.userName || ''}
                              />
                            }
                            headerRightElement={
                              !isDiagnostic &&
                              !(
                                assesment_creation_type?.asessmentType ===
                                  AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT && // to make readymade FAs uneditable
                                assesment_creation_type.creationType ===
                                  'Readymade'
                              ) ? (
                                canEdit(
                                  teacher_id,
                                  fetched_assessment_details
                                ) ? (
                                  <InterractiveButtonGroup
                                    disable={interactiveButtonLoading}
                                    index={index}
                                    handleDeletion={() =>
                                      deletePopupOpen(ques.questionId)
                                    }
                                    handleReplace={() => replaceQuestion(ques)}
                                  />
                                ) : (
                                  <></>
                                )
                              ) : (
                                <></>
                              )
                            }
                            question={ques}
                            showQuestionStats
                            showAnswer
                            imageResizeEnabled={true}
                            customMarks={
                              assesment_creation_type.markingScheme !==
                                MarkingSchemeEnum.MARKING_SCHEME_JEE &&
                              assesment_creation_type.markingScheme !==
                                MarkingSchemeEnum.MARKING_SCHEME_CUSTOM
                            }
                            onMarksChange={(newMarks: number) => {
                              if (!ques.questionId) return;
                              updateTaskMarks(ques.questionId, newMarks);
                            }}
                            hideBloomLevel={
                              assesment_creation_type.markingScheme ===
                              MarkingSchemeEnum.MARKING_SCHEME_JEE
                                ? true
                                : false
                            }
                          />
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
      <ActionsPopup
        open={actionPopupState}
        fontSmall={true}
        iconName="delete"
        popupText="Are you sure you want to delete this question"
        handleClose={() => setActionPopupState(false)}
        noClickHandler={() => setActionPopupState(false)}
        yesClickHandler={() => handleDeleteQuestion(idTobeDeleted)}
      />
    </Box>
  );
}
